import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import {
  Layers, Clock, RefreshCw, Bookmark, Share2, Trash2, Plus, Search, Folder, Star,
  BarChart3, Zap, ChevronDown, ChevronUp, ExternalLink, Globe, Eye
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { sessionIntelligenceService, type SessionIntelligenceSession } from '../services/sessionIntelligence';
import { api } from '../services/client';
import { tabService } from '../services/tabs';
import type { ApiBrowserTab } from '@/types';
import { toast } from 'sonner';
import { PageLayout } from '@/components/layout/PageLayout';

// State carried while a session card is expanded. Kept tiny: just the tab list
// we fetched (capped server-side at ~200) plus a loading / error flag so we
// don't refetch on every render.
interface SessionTabsState {
  tabs: ApiBrowserTab[];
  loading: boolean;
  loaded: boolean;
  error: string | null;
}

const EMPTY_SESSION_TABS: SessionTabsState = {
  tabs: [],
  loading: false,
  loaded: false,
  error: null,
};

// Best-effort host extraction without importing net/url: strips the scheme and
// any path so a long URL renders as something readable in the expanded panel.
function shortHost(raw: string): string {
  if (!raw) return '';
  let s = raw.trim();
  const scheme = (() => {
    const i = s.indexOf('://');
    if (i < 0) return '';
    const out = s.slice(0, i);
    s = s.slice(i + 3);
    return out;
  })();
  const slashIdx = s.indexOf('/');
  const host = slashIdx >= 0 ? s.slice(0, slashIdx) : s;
  return scheme ? `${scheme}://${host}` : host;
}

const SessionIntelligence: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sessions, setSessions] = useState<SessionIntelligenceSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [error, setError] = useState<string>('');

  // Per-card expand state + tab fetch cache. The expandedId is global (only
  // one card open at a time) so users see one focused view; expandSet would
  // be the alternative if you want to compare two sessions side-by-side.
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [tabsBySession, setTabsBySession] = useState<Record<string, SessionTabsState>>({});

  const categories = useMemo(() => ['all', ...Array.from(new Set(sessions.map((s) => s.category))).sort()], [sessions]);

  const loadSessions = async () => {
    setIsLoading(true);
    setError('');
    try {
      const ids = await sessionIntelligenceService.listWorkspaceSessionIds();
      const payloads = await Promise.all(ids.map((id) => sessionIntelligenceService.getSessionIntel(id)));
      setSessions(payloads.map((item) => item.session));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load session intelligence');
      setSessions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadSessions();
  }, []);

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = session.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         session.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || session.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatRelativeTime = (dateRaw: string) => {
    const date = new Date(dateRaw);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const toggleFavorite = (sessionId: string) => {
    setSessions((prev) =>
      prev.map((session) =>
        session.session_id === sessionId ? { ...session, favorite: !session.favorite } : session,
      ),
    );
    toast.info('Favorite state updated locally');
  };

  const toggleExpanded = async (sessionId: string) => {
    const next = expandedId === sessionId ? null : sessionId;
    setExpandedId(next);
    if (!next) return; // collapsing — nothing to fetch
    const existing = tabsBySession[sessionId];
    if (existing?.loaded || existing?.loading) return;
    setTabsBySession((prev) => ({
      ...prev,
      [sessionId]: { tabs: [], loading: true, loaded: false, error: null },
    }));
    try {
      // 200 tabs is enough for the typical workspace session; if the count
      // exceeds this, the "see all in Tab Monitor" button at the bottom of
      // the expand panel is the right next step.
      const res = await tabService.getTabsBySession(sessionId, 1, 200);
      setTabsBySession((prev) => ({
        ...prev,
        [sessionId]: { tabs: res.tabs, loading: false, loaded: true, error: null },
      }));
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load tabs';
      setTabsBySession((prev) => ({
        ...prev,
        [sessionId]: { tabs: [], loading: false, loaded: true, error: msg },
      }));
    }
  };

  const handleRestore = async (sessionId: string) => {
    try {
      await api.put(`/sessions/${sessionId}`, { is_active: true });
      toast.success('Session restored');
      await loadSessions();
    } catch {
      toast.error('Failed to restore session');
    }
  };

  const handleDelete = async (sessionId: string) => {
    try {
      await api.delete(`/sessions/${sessionId}`);
      toast.success('Session deleted');
      await loadSessions();
    } catch {
      toast.error('Failed to delete session');
    }
  };

  const handleCreateSession = async (name: string, description: string) => {
    setIsCreating(true);
    try {
      await sessionIntelligenceService.createSession({ name, description });
      toast.success('Session created');
      setShowCreateModal(false);
      await loadSessions();
    } catch {
      toast.error('Failed to create session');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <PageLayout title="Session Intelligence" description="Smart session management and analysis" icon={Clock}>
      <div className="flex justify-end mb-8">
        <button
          type="button"
          disabled={isLoading || isCreating}
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors shadow-lg disabled:opacity-50 disabled:pointer-events-none"
        >
          <Plus className="h-5 w-5" />
          <span>New Session</span>
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Sessions', value: sessions.length, icon: Layers, bgClass: 'bg-primary-500' },
          { label: 'Total Tabs', value: sessions.reduce((sum, s) => sum + s.tab_count, 0), icon: Folder, bgClass: 'bg-info-500' },
          { label: 'Favorites', value: sessions.filter(s => s.favorite).length, icon: Star, bgClass: 'bg-warning-500' },
          { label: 'Active Today', value: sessions.filter((s) => s.is_active).length, icon: Zap, bgClass: 'bg-success-500' },
        ].map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card hoverable>
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${stat.bgClass}`}>
                    <Icon className="h-5 w-5 text-khiimori-text-primary" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-khiimori-text-primary">
                      {stat.value}
                    </div>
                    <div className="text-xs text-khiimori-text-secondary">
                      {stat.label}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Controls */}
      <div className="mb-6 space-y-4">
        {/* Search and View Toggle */}
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <Card>
              <div className="flex items-center space-x-3">
                <Search className="h-5 w-5 text-khiimori-text-muted" />
                <input
                  type="text"
                  placeholder="Search sessions, tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="flex-1 bg-transparent border-none focus:outline-none text-khiimori-text-primary"
                />
              </div>
            </Card>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-3 rounded-lg transition-all ${
                viewMode === 'grid'
                  ? 'bg-primary-500 text-white'
                  : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary'
              }`}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-3 rounded-lg transition-all ${
                viewMode === 'list'
                  ? 'bg-primary-500 text-white'
                  : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary'
              }`}
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>

        {/* Category Filter */}
        <div className="flex space-x-2 overflow-x-auto pb-2">
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-all ${
                selectedCategory === category
                  ? 'bg-primary-500 text-white shadow-lg'
                  : 'bg-khiimori-bg-tertiary text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary dark:hover:bg-khiimori-bg-tertiary'
              }`}
            >
              {category === 'all' ? 'All Sessions' : category}
            </button>
          ))}
        </div>
      </div>

      {/* Sessions Grid/List */}
      {isLoading && (
        <Card className="mb-6">
          <p className="text-sm text-khiimori-text-secondary">Loading session intelligence...</p>
        </Card>
      )}
      {error && (
        <Card className="mb-6 border-error-400/30 dark:border-error-400/20">
          <p className="text-sm text-error-500 dark:text-error-400">{error}</p>
        </Card>
      )}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSessions.map((session, index) => (
            <motion.div
              key={session.session_id}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card hoverable className="h-full">
                <div className="flex flex-col h-full">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start space-x-3">
                      <div className="text-4xl">{session.thumbnail}</div>
                      <div className="flex-1">
                        <h3 className="font-bold text-khiimori-text-primary mb-1">
                          {session.name}
                        </h3>
                        <div className="text-sm text-khiimori-text-secondary">
                          {session.tab_count} tabs
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => toggleFavorite(session.session_id)}
                      className="p-1 hover:bg-khiimori-bg-elevated rounded transition-colors"
                    >
                      <Star className={`h-5 w-5 ${session.favorite ? 'fill-warning-400 text-warning-400' : 'text-khiimori-text-muted'}`} />
                    </button>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-1 mb-4">
                    {session.tags.map((tag, idx) => (
                      <span key={idx} className="px-2 py-1 bg-primary-500/20 dark:bg-primary-500/30 text-primary-500 dark:text-primary-400 rounded text-xs">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Meta Info */}
                  <div className="space-y-2 text-sm text-khiimori-text-secondary mb-4">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>Last: {formatRelativeTime(session.last_accessed)}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Folder className="h-4 w-4" />
                      <span>{session.category}</span>
                    </div>
                  </div>

                  {/* Expand / hide details */}
                  <button
                    type="button"
                    onClick={() => void toggleExpanded(session.session_id)}
                    className="flex items-center justify-between w-full mb-3 px-3 py-2 rounded-lg bg-khiimori-bg-elevated/40 hover:bg-khiimori-bg-elevated transition-colors text-sm"
                    aria-expanded={expandedId === session.session_id}
                  >
                    <span className="inline-flex items-center space-x-2 text-khiimori-text-primary">
                      <Eye className="h-4 w-4" />
                      <span>{expandedId === session.session_id ? 'Hide details' : 'View tabs'}</span>
                    </span>
                    {expandedId === session.session_id ? (
                      <ChevronUp className="h-4 w-4 text-khiimori-text-secondary" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-khiimori-text-secondary" />
                    )}
                  </button>

                  <AnimatePresence initial={false}>
                    {expandedId === session.session_id && (
                      <motion.div
                        key={`detail-${session.session_id}`}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden mb-4"
                      >
                        <SessionDetailPanel
                          sessionId={session.session_id}
                          sessionName={session.name}
                          state={tabsBySession[session.session_id] ?? EMPTY_SESSION_TABS}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Actions */}
                  <div className="mt-auto pt-4 border-t border-khiimori-border flex items-center justify-between">
                    <button
                      onClick={() => void handleRestore(session.session_id)}
                      className="flex items-center space-x-1 px-3 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors text-sm"
                    >
                      <RefreshCw className="h-4 w-4" />
                      <span>Restore</span>
                    </button>
                    <div className="flex space-x-1">
                      <button
                        disabled
                        title="Share endpoint not available yet"
                        className="p-2 rounded hover:bg-khiimori-bg-elevated transition-colors disabled:opacity-50"
                      >
                        <Share2 className="h-4 w-4 text-khiimori-text-secondary" />
                      </button>
                      <button
                        disabled
                        title="Bookmark endpoint not available yet"
                        className="p-2 rounded hover:bg-khiimori-bg-elevated transition-colors disabled:opacity-50"
                      >
                        <Bookmark className="h-4 w-4 text-khiimori-text-secondary" />
                      </button>
                      <button
                        onClick={() => void handleDelete(session.session_id)}
                        className="p-2 rounded hover:bg-khiimori-bg-elevated transition-colors"
                      >
                        <Trash2 className="h-4 w-4 text-khiimori-text-secondary" />
                      </button>
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredSessions.map((session, index) => (
            <motion.div
              key={session.session_id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card hoverable>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div className="text-3xl">{session.thumbnail}</div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-bold text-khiimori-text-primary">
                          {session.name}
                        </h3>
                        {session.favorite && (
                          <Star className="h-4 w-4 fill-warning-400 text-warning-400" />
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm text-khiimori-text-secondary">
                        <span>{session.tab_count} tabs</span>
                        <span>•</span>
                        <span>{session.category}</span>
                        <span>•</span>
                        <span>Last: {formatRelativeTime(session.last_accessed)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => void toggleExpanded(session.session_id)}
                      className="flex items-center space-x-1 px-3 py-2 rounded-lg hover:bg-khiimori-bg-elevated transition-colors text-sm text-khiimori-text-secondary"
                      aria-expanded={expandedId === session.session_id}
                    >
                      {expandedId === session.session_id ? (
                        <ChevronUp className="h-4 w-4" />
                      ) : (
                        <ChevronDown className="h-4 w-4" />
                      )}
                      <Eye className="h-4 w-4" />
                      <span>Tabs</span>
                    </button>
                    <button
                      onClick={() => void handleRestore(session.session_id)}
                      className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                    >
                      Restore
                    </button>
                    <button
                      disabled
                      title="Share endpoint not available yet"
                      className="p-2 rounded hover:bg-khiimori-bg-elevated transition-colors disabled:opacity-50"
                    >
                      <Share2 className="h-5 w-5 text-khiimori-text-secondary" />
                    </button>
                    <button
                      onClick={() => void handleDelete(session.session_id)}
                      className="p-2 rounded hover:bg-khiimori-bg-elevated transition-colors"
                    >
                      <Trash2 className="h-5 w-5 text-khiimori-text-secondary" />
                    </button>
                  </div>
                </div>
                <AnimatePresence initial={false}>
                  {expandedId === session.session_id && (
                    <motion.div
                      key={`detail-list-${session.session_id}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden mt-3"
                    >
                      <SessionDetailPanel
                        sessionId={session.session_id}
                        sessionName={session.name}
                        state={tabsBySession[session.session_id] ?? EMPTY_SESSION_TABS}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Smart Suggestions */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 }}
        className="mt-8"
      >
        <Card className="bg-gradient-glass border-primary-400/30 dark:border-primary-400/20">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-primary-500 rounded-lg">
              <BarChart3 className="h-6 w-6 text-khiimori-text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-bold text-khiimori-text-primary mb-2">
                Smart Suggestions
              </h3>
              <ul className="space-y-2 text-sm text-khiimori-text-secondary">
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>Your "React Development" session is used most frequently on Tuesdays and Thursdays</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>Consider merging "Email & Communication" with your morning routine session</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>"Research: AI/ML" session hasn't been accessed in 2 days - archive it to declutter?</span>
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </motion.div>

      {showCreateModal && (
        <div className="fixed inset-0 modal-backdrop flex items-center justify-center z-50">
          <div className="glass-card border border-khiimori-border rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">Create New Session</h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                const name = (formData.get('name') as string)?.trim() ?? '';
                const description = (formData.get('description') as string)?.trim() ?? '';
                if (!name) {
                  toast.error('Session name is required');
                  return;
                }
                void handleCreateSession(name, description);
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">Session Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    disabled={isCreating}
                    className="w-full px-3 py-2 border border-khiimori-border rounded-lg bg-khiimori-bg-elevated text-khiimori-text-secondary dark:text-khiimori-text-primary placeholder-khiimori-text-muted focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                    placeholder="e.g., Work Projects"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    name="description"
                    rows={3}
                    disabled={isCreating}
                    className="w-full px-3 py-2 border border-khiimori-border rounded-lg bg-khiimori-bg-elevated text-khiimori-text-secondary dark:text-khiimori-text-primary placeholder-khiimori-text-muted focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
                    placeholder="Describe this session..."
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  disabled={isCreating}
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-khiimori-border rounded-lg text-sm font-medium text-khiimori-text-secondary hover:bg-khiimori-bg-tertiary disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 disabled:opacity-50"
                >
                  {isCreating ? 'Creating…' : 'Create Session'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

// SessionDetailPanel renders the analytics summary + tab list for a single
// session card when expanded. Sourced from `tabService.getTabsBySession` so
// the same `browser_tabs.session_id` rows that back the Tab Monitor appear
// here — no duplicate fetch path.
function SessionDetailPanel({
  sessionId,
  sessionName,
  state,
}: {
  sessionId: string;
  sessionName: string;
  state: SessionTabsState;
}) {
  if (state.loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-6 text-sm text-khiimori-text-muted">
        <RefreshCw className="h-4 w-4 animate-spin" />
        <span>Loading tabs…</span>
      </div>
    );
  }
  if (state.error) {
    return (
      <div className="rounded-lg border border-error-400/30 bg-error-400/5 p-3 text-xs text-error-400">
        {state.error}
      </div>
    );
  }
  if (state.tabs.length === 0) {
    return (
      <div className="rounded-lg border border-khiimori-border/40 bg-khiimori-bg-elevated/30 p-4 text-sm text-khiimori-text-secondary">
        This session has no tabs recorded yet. Open tabs in the workspace, or
        visit{' '}
        <a
          href="/monitor"
          className="text-primary-500 hover:underline"
        >
          Tab Monitor
        </a>{' '}
        to start browsing.
      </div>
    );
  }

  // Compress long URL hosts so the panel fits a 3-column grid.
  const tabs = state.tabs;
  const closedCount = tabs.filter((t) => t.status === 'closed').length;
  const activeCount = tabs.length - closedCount;

  return (
    <div className="space-y-3">
      {/* Analytics strip */}
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="rounded-md bg-khiimori-bg-elevated/40 px-2 py-2">
          <div className="text-lg font-semibold text-khiimori-text-primary">{activeCount}</div>
          <div className="text-[10px] uppercase tracking-wide text-khiimori-text-muted">active</div>
        </div>
        <div className="rounded-md bg-khiimori-bg-elevated/40 px-2 py-2">
          <div className="text-lg font-semibold text-khiimori-text-primary">{closedCount}</div>
          <div className="text-[10px] uppercase tracking-wide text-khiimori-text-muted">closed</div>
        </div>
        <div className="rounded-md bg-khiimori-bg-elevated/40 px-2 py-2">
          <div className="text-lg font-semibold text-khiimori-text-primary">{tabs.length}</div>
          <div className="text-[10px] uppercase tracking-wide text-khiimori-text-muted">total</div>
        </div>
      </div>

      {/* Tab list — scrollable so a session with 100+ tabs doesn't blow up the page */}
      <ul className="max-h-80 overflow-y-auto rounded-md border border-khiimori-border/40 bg-khiimori-bg-elevated/20 divide-y divide-khiimori-border/30">
        {tabs.map((tab) => (
          <li key={tab.id} className="flex items-start gap-2 px-3 py-2 text-xs">
            {tab.favicon ? (
              <img
                src={tab.favicon}
                alt=""
                className="h-4 w-4 mt-0.5 flex-shrink-0 rounded"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <Globe className="h-4 w-4 mt-0.5 flex-shrink-0 text-khiimori-text-muted" />
            )}
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-khiimori-text-primary">
                {tab.title || shortHost(tab.url) || 'Untitled tab'}
              </div>
              <div className="truncate text-khiimori-text-muted" title={tab.url}>
                {shortHost(tab.url)}
              </div>
            </div>
            <span
              className={`flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] uppercase ${
                tab.status === 'closed'
                  ? 'bg-khiimori-bg-tertiary text-khiimori-text-muted'
                  : 'bg-success-500/15 text-success-500'
              }`}
              title={tab.url}
            >
              {tab.status === 'closed' ? 'closed' : 'open'}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between text-xs">
        <span className="text-khiimori-text-muted">
          Showing {tabs.length} tab{tabs.length === 1 ? '' : 's'} from <strong>{sessionName}</strong>
        </span>
        <a
          href="/monitor"
          className="inline-flex items-center gap-1 text-primary-500 hover:underline"
        >
          Open in Tab Monitor <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}

export default SessionIntelligence;